import { debugAtom, debugStore } from "@folio-2026/experience"
import Camera from "@folio-2026/section/camera"
import { EVENTS } from "@folio-2026/shared"
import assert from "assert"
import gsap from "gsap"
import * as THREE from "three/webgpu"

import { cameraMovements } from "./const"

export default class WorksSectionCamera extends Camera {
  pos: THREE.Vector3
  look: THREE.Vector3
  currentLook: THREE.Vector3

  enteringAnimation: gsap.core.Timeline
  scrollingAnimation: gsap.core.Timeline
  leavingAnimation: gsap.core.Timeline

  private isOrbitControls: boolean = false
  rollAngle: number = 0 // Rotation angle around camera forward direction

  alreadyShaken: boolean = false

  constructor({ scene }: { scene: THREE.Scene }) {
    super({ scene })

    const camMov = cameraMovements.get("initial")
    this.pos = new THREE.Vector3(
      camMov.positionX,
      camMov.positionY,
      camMov.positionZ,
    )
    this.look = new THREE.Vector3(
      camMov.lookAtX,
      camMov.lookAtY,
      camMov.lookAtZ,
    )
    this.currentLook = new THREE.Vector3(
      camMov.lookAtX,
      camMov.lookAtY,
      camMov.lookAtZ,
    )

    this.enteringAnimation = this.createEnteringAnimation()
    this.scrollingAnimation = this.createScrollingAnimation()
    this.leavingAnimation = this.createLeavingAnimation()

    this.setDebug()
    this.eventListeners()
  }

  eventListeners() {
    this.experience.on(EVENTS.Works.BeforeEnter, () => {
      this.alreadyShaken = false
    })

    this.experience.on(EVENTS.Works.Entering, this.enterAnimation.bind(this))

    this.experience.on(EVENTS.Works.Scroll, this.scrollHandler.bind(this))

    this.experience.on(
      EVENTS.Guestbook.Entering,
      this.leaveAnimation.bind(this),
    )

    this.experience.on(EVENTS.Works.Entered, this.shakeAnimation.bind(this))
  }

  shakeAnimation() {
    if (this.alreadyShaken) {
      return
    }

    this.alreadyShaken = true

    // Wave settings
    const maxAngle = 0.15 // Maximum roll angle in radians
    const waveDuration = 0.3 // Duration per wave

    const tl = gsap.timeline()

    // First wave - rotate left
    tl.to(this, {
      duration: waveDuration,
      rollAngle: -maxAngle,
      ease: "sine.inOut",
    })

    // First wave - rotate right (with decreasing intensity)
    const angle1 = maxAngle * 0.7
    tl.to(
      this,
      {
        duration: waveDuration,
        rollAngle: angle1,
        ease: "sine.inOut",
      },
      waveDuration,
    )

    // Second wave - rotate left (with further decreasing intensity)
    const angle2 = maxAngle * 0.4
    tl.to(
      this,
      {
        duration: waveDuration,
        rollAngle: -angle2,
        ease: "sine.inOut",
      },
      waveDuration * 2,
    )

    // Return to original rotation
    const returnDuration = 1 - waveDuration * 3
    tl.to(
      this,
      {
        duration: returnDuration,
        rollAngle: 0,
        ease: "power2.out",
      },
      waveDuration * 3,
    )
  }

  enterAnimation(event: unknown) {
    assert(this.enteringAnimation, "Works entering animation is not defined")

    const { progress } = event as { progress: number }

    this.enteringAnimation.seek(progress)
  }

  leaveAnimation(event: unknown) {
    assert(this.leavingAnimation, "Works leaving animation is not defined")

    const { progress } = event as { progress: number }

    this.leavingAnimation.seek(progress)
  }

  scrollHandler(event: unknown) {
    assert(this.scrollingAnimation, "Works scrolling animation is not defined")

    const { progress } = event as { progress: number }

    this.scrollingAnimation.seek(progress)
  }

  createScrollingAnimation() {
    const camMov = cameraMovements.get("section-end")

    const tl = gsap.timeline({
      paused: true,
    })

    tl.to(this.pos, {
      duration: 1,
      ease: "power1.in",
      x: camMov.positionX,
      y: camMov.positionY,
      z: camMov.positionZ,
    }).to(
      this.look,
      {
        duration: 1,
        ease: "power2.in",
        x: camMov.lookAtX,
        y: camMov.lookAtY,
        z: camMov.lookAtZ,
      },
      0,
    )

    return tl
  }

  createLeavingAnimation() {
    const camMov = cameraMovements.get("section-leaving")

    const tl = gsap.timeline({
      paused: true,
    })

    const tlPos = gsap.timeline().to(this.pos, {
      duration: 1,
      x: camMov.positionX,
      y: camMov.positionY,
      z: camMov.positionZ,
    })

    const tlLook = gsap.timeline().to(this.look, {
      duration: 1,
      x: camMov.lookAtX,
      y: camMov.lookAtY,
      z: camMov.lookAtZ,
    })

    tl.add(tlPos, 0).add(tlLook, 0)

    return tl
  }

  /**
   * Sets the enter animation timeline
   * This is called when the section gets initialized
   */
  createEnteringAnimation() {
    const camMov = cameraMovements.get("section-entered")

    const tl = gsap.timeline({
      paused: true,
    })

    const tlPos = gsap.timeline().to(this.pos, {
      duration: 1,
      x: camMov.positionX,
      y: camMov.positionY,
      z: camMov.positionZ,
    })

    const tlLook = gsap.timeline().to(this.look, {
      duration: 1,
      x: camMov.lookAtX,
      y: camMov.lookAtY,
      z: camMov.lookAtZ,
    })

    tl.add(tlPos, 0).add(tlLook, 0)

    return tl
  }

  update() {
    super.update()

    if (this.isOrbitControls) {
      return
    }

    this.lerpPosition()
    this.calculateOffset()
    this.updateCameraPosition()
    this.updateCameraLookAt()
  }

  private updateCameraPosition() {
    // Apply mouse offset to animated position
    const positionX = this.pos.x + this.mouseOffset.x
    const positionY = this.pos.y + -this.mouseOffset.y
    const positionZ = this.pos.z + this.mouseOffset.x

    this.camera.position.set(positionX, positionY, positionZ)
  }

  private updateCameraLookAt() {
    // Apply mouse offset to animated look at position
    const lookAtX = this.look.x + this.mouseOffset.x * 1.5
    const lookAtY = this.look.y + -this.mouseOffset.y * 1.5
    const lookAtZ = this.look.z + this.mouseOffset.x
    // Calculate forward direction
    const forward = new THREE.Vector3()
      .subVectors(
        new THREE.Vector3(lookAtX, lookAtY, lookAtZ),
        new THREE.Vector3(
          this.camera.position.x,
          this.camera.position.y,
          this.camera.position.z,
        ),
      )
      .normalize()

    // Default up vector
    const defaultUp = new THREE.Vector3(0, 1, 0)

    // Calculate right vector (perpendicular to forward and up)
    const right = new THREE.Vector3()
      .crossVectors(forward, defaultUp)
      .normalize()

    // Calculate the actual up vector (perpendicular to forward and right)
    const up = new THREE.Vector3().crossVectors(right, forward).normalize()

    // Apply roll rotation around forward axis
    if (this.rollAngle !== 0) {
      const rotationAxis = forward.clone()
      const rotationQuaternion = new THREE.Quaternion().setFromAxisAngle(
        rotationAxis,
        this.rollAngle,
      )

      // Rotate the up vector around the forward axis
      up.applyQuaternion(rotationQuaternion)
    }

    // Set camera to look at target with rotated up vector
    this.camera.up.copy(up)
    this.camera.lookAt(lookAtX, lookAtY, lookAtZ)
  }

  reset() {
    this.camera.position.z = 1.5
  }

  setDebug() {
    debugStore.sub(debugAtom, () => {
      const state = debugStore.get(debugAtom)

      this.isOrbitControls = state.showOrbitControls
    })
  }
}
